<template>

    <div id="account">

        <div class="flex">
            <div class="flex-1 mb-4">
                <h1 class="text-2xl font-medium text-gray-900 capitalize">Accounts</h1>
            </div>

            <div class="flex mb-4 justify-end">
                <router-link :to="{ name: 'cost-indexes' }" custom v-slot="{ navigate }">
                    <button @click="navigate" class="btn">
                        Cost Indexes
                    </button>
                </router-link>
            </div>
        </div>

        <p class="text-sm mb-4 text-gray-500">
            Selecting a cost index for an account will approve the account.
        </p>

        <div class="flex flex-col"  v-if="loaded">

            <div class="index-table" v-if="form.indexDataAvailable()">
                <div>
                    <div>
                        <table>
                            <thead>
                            <tr>
                                <th scope="col" class="hidden lg:table-cell text-left tracking-wider">
                                    &nbsp;
                                </th>
                                <th scope="col" class="text-left tracking-wider">
                                    Account Name
                                </th>
                                <th scope="col" class="hidden lg:table-cell text-left tracking-wider">
                                    Primary Account User
                                </th>
                                <th scope="col" class="text-center tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="hidden lg:table-cell text-center tracking-wider">
                                    Approval Date
                                </th>
                                <th scope="col" class="relative p-3">
                                    Cost Index
                                </th>
                                <th scope="col" class="text-center tracking-wider">
                                    Action
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr class="colored-row" v-for="(account, index) in form.index_data.data">
                                <td class="hidden lg:table-cell">&nbsp;</td>
                                <td>
                                    <router-link :to="{ name: 'accounts.show', params: { 'id': account.id } }" class="text-brand-700 hover:text-brand-800" v-if="$parent.isAdmin">
                                        {{ account.name }}
                                        <span v-if="account.tenant_name">({{account.tenant_name}})</span>
                                    </router-link>
                                    <br>
                                    <span class="text-gray-400">{{ formatDateShort(account.created_at) }}</span>
                                </td>
                                <td class="hidden lg:table-cell">
                                    <span v-if="account.primary_users_email">
                                        <span class="text-gray-700">{{ account.primary_users_name }}</span><br>
                                        <span class="text-gray-400">{{ account.primary_users_email }}</span>
                                    </span>
                                    <span v-else>No Account User (Deleted)</span>
                                </td>
                                <td class="text-center">
                                    <span class="status-box status-complete" v-if="account.active">
                                        Active
                                    </span>
                                    <span class="status-box status-cancelled" v-else>
                                        Inactive
                                    </span>
                                </td>
                                <td class="hidden lg:table-cell text-center">
                                    <span v-if="account.active">
                                        Account approved on<br>
                                        <span class="text-gray-400">{{ formatDate(account.primary_users_email_verified_at) }}</span>
                                    </span>
                                    <span v-else>-</span>
                                </td>
                                <td class="text-right">
                                    <div class="form-control col-span-6 xl:col-span-4">
                                        <select name="account_id" id="account_id" v-model="account.cost_index" @change="updateCI($event, index)">
                                            <option :value="null">Select Cost Index</option>
                                            <option :value="cost_index.id" v-for="cost_index in form.cost_indexes.data">{{ cost_index.id }}</option>
                                        </select>
                                    </div>
                                </td>
                                <td class="text-right">
                                    <button class="btn-error text-xs" @click="deleteAccount(account.id)">
                                        Delete Account
                                    </button>
                                </td>
                            </tr>
                            <tr class="py-6 text-xs font-medium">
                                <td colspan="7" class="text-center">
                                    <infinite-loading @infinite="infiniteHandler">
                                        <div slot="spinner">Loading...</div>
                                        <div slot="no-more">No more Accounts</div>
                                    </infinite-loading>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div class="panel" v-else>
                <div class="p-10">
                    <div class="text-gray-200 my-6">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-20 w-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                    </div>

                    <h2 class="text-gray-600 font-medium text-2xl text-center">No Accounts Available</h2>

                    <p class="text-center mt-4 text-sm text-gray-500">
                        We could not find any accounts.
                    </p>
                </div>
            </div>
        </div>
        <Loading message="Loading Accounts" v-else></Loading>
    </div>

</template>

<script>
import Loading from "../Support/Loading";
import {Account} from "../../app/Auth/Account";
import InfiniteLoading from "vue-infinite-loading";
import {FormattingMixin, PermissionMixin} from "../../support/mixins";

export default {
    name: "Accounts",
    mixins: [PermissionMixin, FormattingMixin],
    data() {
        return {
            form: null,
            loaded: false,
        }
    },
    components: {
        Loading,
        InfiniteLoading
    },
    methods: {
        updateCI(event, index) {
            let vm = this;

            vm.form.updateCostIndex(event, index).then(result => {
                vm.$forceUpdate();
            });
        },
        infiniteHandler($state) {
            let vm = this;

            vm.form.page += 1;

            if (vm.form.indexHasMoreResults()) {
                this.form.index(true).then(response => {
                    $state.loaded();
                });
            } else {
                $state.loaded();
                $state.complete();
            }
        },
        deleteAccount(index)
        {
            if (confirm('Are you sure you want to delete this account?')) {
                let vm = this;
                vm.form.deleteAccount(index).then(() => {
                    resolve(true);

                }).catch(error => vm.handleError(error));
                
            }
        },
        // This is the method that handles the error
        handleError(error) {
            // Check if the error has a response from the server
            if (error.response && error.response.data && error.response.data.message) {                
                alert(error.response.data.message);
            }
        },
    },
    mounted() {
        let vm = this;

        new Promise(resolve => {
            vm.form = new Account();

            vm.form.index().then(response => {
                vm.form.getCostIndexes().then(result => {
                    resolve(true);
                });
            }).catch(error => vm.handleError(error));

        }).then(result => {
            vm.loaded = result;
        });
    }
}
</script>


